import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Annotation } from "@/utils/imageProcessing";

interface AnnotationToolsProps {
  activeTool: string;
  color: string;
  onColorChange: (color: string) => void;
  thickness: number;
  onThicknessChange: (thickness: number) => void;
  annotations: Annotation[];
  onDeleteAnnotation: (id: string) => void;
}

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#3b82f6', '#8b5cf6', '#ec4899', '#000000', '#ffffff'
];

export const AnnotationTools = ({
  activeTool,
  color,
  onColorChange,
  thickness,
  onThicknessChange,
  annotations,
  onDeleteAnnotation,
}: AnnotationToolsProps) => {
  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm">Annotation Settings</h3>

      <div className="space-y-2">
        <Label className="text-xs">Color</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map((presetColor) => (
            <button
              key={presetColor}
              className="w-8 h-8 rounded border-2"
              style={{
                backgroundColor: presetColor,
                borderColor: color === presetColor ? '#000' : 'transparent',
              }}
              onClick={() => onColorChange(presetColor)}
            />
          ))}
        </div>
        <Input
          type="color"
          value={color}
          onChange={(e) => onColorChange(e.target.value)}
          className="w-full h-10"
        />
      </div>

      {(activeTool === 'arrow' || activeTool === 'rectangle' || activeTool === 'circle' || activeTool === 'freehand') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Thickness</Label>
            <span className="text-xs text-muted-foreground">{thickness}px</span>
          </div>
          <Slider
            value={[thickness]}
            onValueChange={([value]) => onThicknessChange(value)}
            min={1}
            max={20}
            step={1}
            className="w-full"
          />
        </div>
      )}

      {annotations.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Annotations ({annotations.length})</Label>
          <ScrollArea className="h-[150px]">
            <div className="space-y-2">
              {annotations.map((annotation, idx) => (
                <div
                  key={annotation.id}
                  className="flex items-center justify-between p-2 bg-muted rounded text-xs"
                >
                  <span className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: annotation.color }}
                    />
                    {annotation.type} #{idx + 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteAnnotation(annotation.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
